import { Component, inject } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormBuilder, ReactiveFormsModule, Validators } from '@angular/forms';
import { firstValueFrom } from 'rxjs';
import { HttpClient } from '@angular/common/http';
import { JwtService } from '../../services/jwt.service';
import { Router, RouterLink } from '@angular/router';
import { NzFormModule } from 'ng-zorro-antd/form';
import { NzInputModule } from 'ng-zorro-antd/input';
import { NzButtonModule } from 'ng-zorro-antd/button';
import { NzCardModule } from 'ng-zorro-antd/card';
import { NzGridModule } from 'ng-zorro-antd/grid';
import { NzIconModule } from 'ng-zorro-antd/icon';
import { environment } from '../../../environments/environment';
import { TranslatePipe } from '../../pipes/translate.pipe';
import { UsersService } from '../../services/users.service';

@Component({
  selector: 'app-login',
  imports: [
    CommonModule,
    ReactiveFormsModule,
    RouterLink,
    NzFormModule,
    NzInputModule,
    NzButtonModule,
    NzCardModule,
    NzGridModule,
    NzIconModule,
    TranslatePipe,
  ],
  templateUrl: './login.component.html',
  styleUrls: ['./login.component.scss'],
})
export class LoginComponent {
  jwtService = inject(JwtService);
  router = inject(Router);
  http = inject(HttpClient);
  fB = inject(FormBuilder);
  usersService = inject(UsersService);

  form = this.fB.group({
    login: ['', Validators.required],
    password: ['', Validators.required],
  });

  /** Drives the eye toggle next to the password input. */
  passwordVisible = false;
  /** i18n key of the inline error shown above the form. Null when no
   *  attempt has failed yet — keeps the form free of clutter on first paint. */
  errorMessageKey: string | null = null;
  /** Blocks double-submits while the request is in flight. */
  submitting = false;

  constructor() {
    // Auto-clear the inline error as soon as the user starts correcting
    // their input — avoids the lingering red banner when they're already
    // typing the right value.
    this.form.valueChanges.subscribe(() => {
      if (this.errorMessageKey) {
        this.errorMessageKey = null;
      }
    });
  }

  async login() {
    if (this.form.invalid || this.submitting) {
      return;
    }
    this.errorMessageKey = null;
    this.submitting = true;
    try {
      const url = new URL('/auth/login', environment.apiUrl).href;
      const res = await firstValueFrom(
        this.http.post<{ access_token: string }>(url, {
          login: this.form.value.login,
          password: this.form.value.password,
        })
      );

      this.jwtService.setToken(res.access_token);

      // Check if it's a device user and navigate accordingly
      if (this.jwtService.deviceToken && !this.jwtService.jwtToken) {
        // Device user - go to PIN entry
        this.router.navigateByUrl('/pin-entry');
      } else {
        // Regular user — but `superAdmin` belongs to the backoffice only;
        // refuse them at the gate and drop the token so they can't proceed.
        const me = await firstValueFrom(this.usersService.getMe());
        const roleName = (me?.roleId?.name ?? me?.role?.name) as string | undefined;
        if (roleName === 'superAdmin') {
          this.jwtService.clearEmployeeToken();
          this.errorMessageKey = 'login.errors.superAdminNotAllowed';
          return;
        }
        this.router.navigateByUrl('/dashboard');
      }
    } catch (err: any) {
      // Standard "invalid credentials" message regardless of whether the
      // backend says "user not found" (404) or "wrong password" (401) —
      // collapsing both prevents email-enumeration attacks.
      //   Status   When
      //   ───────  ─────────────────────────────────────────────────
      //   401      Wrong password (Passport returns 401 Unauthorized)
      //   404      Email not found (some implementations)
      //   400      Validation error on the payload
      //
      // Anything else (5xx server crash, network failure / backend down /
      // CORS, timeout, non-JSON response from a proxy…) falls back to the
      // generic message so the user knows it's NOT their fault.
      const status = err?.status;
      this.errorMessageKey =
        status === 401 || status === 404 || status === 400
          ? 'login.errors.invalidCredentials'
          : 'login.errors.generic';
    } finally {
      this.submitting = false;
    }
  }
}
